import { createFileRoute, redirect } from '@tanstack/react-router';
import { SessionErrorPage } from '@/components/shell/SessionErrorPage';
import { sessionQuery } from '@/lib/use-session';
import { takeAuthDrift } from '@/lib/queries/identity';
import { readRememberedLogin } from '@/lib/remembered-login';

// Landing route. Authed users → /feed. Logged-out visitors land in the
// public experience (`/join-us`, the top tab of the anonymous sidebar) so a
// first-time visitor can browse Circles/Networks/Resources before committing
// — rather than being dropped straight onto the signup form. A visitor who
// asked to be remembered on this device is a returning user, so they go to
// /auth/login instead (their email is prefilled there).
//
// Error classification: the session probe resolving NULL is the
// definitive signed-out verdict. The probe THROWING (network failure,
// server 5xx — react-query has already exhausted its retries) means the
// auth state is UNKNOWN → the error propagates to `errorComponent`, which
// renders a retryable surface. Routing fetch errors here treated
// "couldn't determine" as "signed out" and logged returning users out
// during deploys/outages.
export const Route = createFileRoute('/')({
  beforeLoad: async ({ context }) => {
    const data = await context.queryClient.ensureQueryData(sessionQuery);
    if (data !== null) throw redirect({ to: '/feed' });
    // Drift-final sign-out (restricted / past-window deactivation): this
    // visitor HAD an account, so the public-browse landing is wrong — send
    // them to login where the reason banner renders.
    const reason = takeAuthDrift();
    if (reason !== null) {
      throw redirect({ to: '/auth/login', search: { reason } });
    }
    // Remembered on this device → a returning user; send them to the
    // prefilled login rather than the prospective-member landing.
    if (readRememberedLogin() !== null) {
      throw redirect({ to: '/auth/login' });
    }
    throw redirect({ to: '/join-us' });
  },
  errorComponent: SessionErrorPage,
});
